import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  calculateRoute,
  plannedDepartureDefault,
  plannedDepartureToIso,
  RouteApiError,
} from '../lib/route';
import type { Coordinate, RouteResult } from '../types';

interface RoutePlannerProps {
  destination: Coordinate;
  origin: Coordinate | null;
  pickingOrigin: boolean;
  onOriginChange: (origin: Coordinate | null) => void;
  onPickingOriginChange: (picking: boolean) => void;
  onRouteChange: (route: RouteResult | null) => void;
}

const formatDuration = (seconds: number, locale: string) => {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return locale.startsWith('es') ? `${hours} h ${minutes} min` : `${hours}h ${minutes}m`;
};

export function RoutePlanner({
  destination,
  origin,
  pickingOrigin,
  onOriginChange,
  onPickingOriginChange,
  onRouteChange,
}: RoutePlannerProps) {
  const { t, i18n } = useTranslation();
  const [departureMode, setDepartureMode] = useState<'now' | 'planned'>('planned');
  const [plannedDeparture, setPlannedDeparture] = useState(plannedDepartureDefault());
  const [result, setResult] = useState<RouteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const plannedDepartureIso = plannedDepartureToIso(plannedDeparture);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const useCurrentLocation = () => {
    setError(null);
    if (!navigator.geolocation) {
      setError(t('route.permissionDenied'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onOriginChange({ lat: position.coords.latitude, lon: position.coords.longitude });
        onPickingOriginChange(false);
      },
      () => setError(t('route.permissionDenied')),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  const submit = async () => {
    if (!origin) return;
    if (departureMode === 'planned' && !plannedDepartureIso) {
      setError(t('route.invalidDeparture'));
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    setResult(null);
    onRouteChange(null);
    try {
      const route = await calculateRoute(
        {
          origin,
          destination,
          departAt: departureMode === 'now' ? 'now' : (plannedDepartureIso ?? 'now'),
        },
        controller.signal,
      );
      setResult(route);
      onRouteChange(route);
    } catch (reason) {
      if (controller.signal.aborted) return;
      const key =
        reason instanceof RouteApiError
          ? reason.code === 'QUOTA_EXCEEDED'
            ? 'route.quota'
            : reason.code === 'NOT_CONFIGURED'
              ? 'route.notConfigured'
              : reason.code === 'OUTSIDE_COVERAGE'
                ? 'route.outside'
                : 'route.unavailable'
          : 'route.unavailable';
      setError(t(key));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  return (
    <section className="route-planner">
      <div className="route-origin-actions">
        <button className="secondary-button" type="button" onClick={useCurrentLocation}>
          <span aria-hidden="true">⌖</span> {t('route.useLocation')}
        </button>
        <button
          className={`secondary-button ${pickingOrigin ? 'active' : ''}`}
          type="button"
          onClick={() => onPickingOriginChange(!pickingOrigin)}
        >
          <span aria-hidden="true">＋</span>{' '}
          {origin ? t('route.originReady') : t('route.pickOrigin')}
        </button>
      </div>
      <fieldset className="segmented-control">
        <legend>{t('route.departure')}</legend>
        <label>
          <input
            type="radio"
            name="departure"
            value="now"
            checked={departureMode === 'now'}
            onChange={() => setDepartureMode('now')}
          />
          <span>{t('route.now')}</span>
        </label>
        <label>
          <input
            type="radio"
            name="departure"
            value="planned"
            checked={departureMode === 'planned'}
            onChange={() => setDepartureMode('planned')}
          />
          <span>{t('route.planned')}</span>
        </label>
      </fieldset>
      {departureMode === 'planned' && (
        <input
          className="date-input"
          type="datetime-local"
          value={plannedDeparture}
          min="2026-07-01T00:00"
          max="2026-08-12T23:59"
          required
          aria-label={t('route.departureTime')}
          onChange={(event) => setPlannedDeparture(event.target.value)}
        />
      )}
      {departureMode === 'planned' && (
        <>
          <small className="timezone-hint">{t('route.spainTime')}</small>
          <p className="warning-copy">{t('route.exceptionalWarning')}</p>
        </>
      )}
      <button
        className="primary-button route-submit"
        type="button"
        disabled={!origin || loading || (departureMode === 'planned' && !plannedDepartureIso)}
        onClick={() => void submit()}
      >
        {loading ? t('route.calculating') : t('route.calculate')}
      </button>
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      {result && (
        <div className="route-result" aria-live="polite">
          <div>
            <strong>
              {t('route.eta', { value: formatDuration(result.travelTimeSeconds, i18n.language) })}
            </strong>
            <span>{t('route.distance', { value: (result.distanceMeters / 1000).toFixed(1) })}</span>
          </div>
          <div>
            <strong>
              {result.trafficMode === 'live' ? t('route.live') : t('route.historical')}
            </strong>
            <span>{t('route.delay', { value: Math.round(result.trafficDelaySeconds / 60) })}</span>
          </div>
        </div>
      )}
    </section>
  );
}
